// Pestaña «Bitácora» de la ficha: las llamadas al proveedor de ESTE equipo, de la más reciente a la
// más vieja. Cada fila se expande al folio, al request y al response crudos.
import { Fragment, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { MdmOperationStatusEnum } from '@fiado/type-kit/bin/mdm/enums/MdmOperationStatusEnum';
import { cn } from '@/lib/cn';
import { formatDateMx, formatDateTimeSecondsMx, formatTimeMx } from '@/lib/dateHelpers';
import { callCountLabel, resolveOperationStatusPresentation } from '../logic';
import { BreakableCode } from './BreakableCode';
import type { MdmAuditEntry, MdmFleetDevice } from '../types';

export interface MdmAuditTabProps {
    device: MdmFleetDevice;
    /** El audit log completo: la pestaña se queda con las llamadas de ESTE crédito. */
    entries: readonly MdmAuditEntry[];
    /** La bitácora todavía se está leyendo: no hay nada que afirmar sobre este equipo. */
    isLoading?: boolean;
    /** La lectura falló (403, 500, timeout). Se pinta como error, nunca como lista vacía. */
    error?: Error | null;
}

/** Lo que ve la celda de error cuando la línea no trae motivo; cada desenlace dice algo distinto. */
const NO_ERROR_VALUE = '—';
const UNKNOWN_OUTCOME_TITLE = 'La bitácora no guardó ningún motivo para esta llamada';
const NO_ERROR_TITLE: Record<MdmOperationStatusEnum, string> = {
    [MdmOperationStatusEnum.SUCCESS]: 'La llamada no devolvió error',
    [MdmOperationStatusEnum.PENDING]:
        'La llamada sigue abierta: todavía no hay desenlace que registrar',
    [MdmOperationStatusEnum.ERROR]: 'La llamada falló y el proveedor no dijo por qué',
};

// La tabla no guarda el request ni el response de la llamada: no es que falten en esta línea.
const NO_PAYLOAD_NOTE =
    'La bitácora no guarda el request ni el response: ninguna llamada los registra.';

// Pisan el `h-11 px-4` del Table compartido: con ese padding las columnas no entran en la ficha.
const HEAD_CLASS = 'h-8 truncate px-0.5 text-[9.5px] font-bold uppercase tracking-[0.04em]';
const CELL_CLASS = 'px-0.5 py-1.5 align-top text-[11px]';
const PRE_LABEL = 'text-[9.5px] font-bold uppercase tracking-[0.04em] text-muted-foreground';
/** La nota del payload es una oración: las versalitas del rótulo la vuelven ilegible. */
const PRE_NOTE = 'text-[10px] italic leading-tight text-muted-foreground';

/** Anchos en porcentaje: el panel de la ficha se angosta con la ventana y una suma fija lo desborda. */
const COLUMN_WIDTH = {
    at: 'w-[24%]',
    operation: 'w-[20%]',
    status: 'w-[16%]',
    error: 'w-[40%]',
} as const;

/** El código del error va al tooltip: la celda muestra el texto humano que manda el proveedor. */
function errorTitle(entry: MdmAuditEntry): string {
    if (entry.errorCode) return entry.errorCode;
    if (entry.errorMessage) return entry.errorMessage;
    // El cliente no valida la respuesta: un desenlace fuera del enum no autoriza a afirmar nada.
    const byStatus: Partial<Record<MdmOperationStatusEnum, string>> = NO_ERROR_TITLE;
    return byStatus[entry.status] ?? UNKNOWN_OUTCOME_TITLE;
}

export function MdmAuditTab({
    device,
    entries,
    isLoading = false,
    error = null,
}: MdmAuditTabProps) {
    const [expandedCallId, setExpandedCallId] = useState<string | null>(null);

    const deviceEntries = entries
        .filter((entry) => entry.creditId === device.creditId)
        .sort((a, b) => b.at.localeCompare(a.at));

    // El contador solo se muestra cuando hay una respuesta: «0 llamadas» sobre un error mentiría.
    const answered = error === null && !isLoading;

    return (
        <section className="overflow-hidden rounded-lg border border-border bg-card">
            <header className="flex items-center justify-between gap-2 border-b border-border bg-muted/40 px-3 py-2">
                <h3 className="text-[11.5px] font-bold uppercase tracking-[0.04em]">
                    Audit log del dispositivo
                </h3>
                {answered ? (
                    <span className="text-[10px] text-muted-foreground">
                        {callCountLabel(deviceEntries.length)}
                    </span>
                ) : null}
            </header>

            {error !== null ? (
                <p role="alert" className="px-3 py-6 text-center text-[11.5px] text-destructive">
                    No se pudo cargar la bitácora de este equipo. Vuelve a intentarlo en unos
                    minutos.
                </p>
            ) : isLoading ? (
                <p
                    role="status"
                    className="px-3 py-6 text-center text-[11.5px] italic text-muted-foreground"
                >
                    Cargando bitácora…
                </p>
            ) : deviceEntries.length === 0 ? (
                <p className="px-3 py-6 text-center text-[11.5px] italic text-muted-foreground">
                    Sin registros para este device
                </p>
            ) : (
                <Table className="table-fixed">
                    <TableHeader>
                        <TableRow>
                            <TableHead className={cn(HEAD_CLASS, COLUMN_WIDTH.at)}>Fecha</TableHead>
                            <TableHead className={cn(HEAD_CLASS, COLUMN_WIDTH.operation)}>
                                Operation
                            </TableHead>
                            <TableHead className={cn(HEAD_CLASS, COLUMN_WIDTH.status)}>
                                Estado
                            </TableHead>
                            <TableHead className={cn(HEAD_CLASS, COLUMN_WIDTH.error)}>
                                Error
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {deviceEntries.map((entry) => {
                            const expanded = expandedCallId === entry.callId;
                            const panelId = `audit-detail-${entry.callId}`;
                            const statusPresentation = resolveOperationStatusPresentation(
                                entry.status,
                            );
                            return (
                                <Fragment key={entry.callId}>
                                    <TableRow>
                                        <TableCell
                                            title={formatDateTimeSecondsMx(entry.at)}
                                            className={CELL_CLASS}
                                        >
                                            {/* Fecha arriba y hora abajo: juntas piden 40 px más de los que la ficha tiene. */}
                                            <button
                                                type="button"
                                                aria-expanded={expanded}
                                                aria-controls={panelId}
                                                onClick={() => {
                                                    setExpandedCallId(
                                                        expanded ? null : entry.callId,
                                                    );
                                                }}
                                                className="flex w-full flex-col whitespace-nowrap rounded text-left leading-tight tabular-nums hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                            >
                                                <span className="text-[10px]">
                                                    {formatDateMx(entry.at)}
                                                </span>
                                                <span className="flex items-center gap-0.5 text-[9.5px] text-muted-foreground">
                                                    {expanded ? (
                                                        <ChevronDown
                                                            className="size-2.5 shrink-0"
                                                            aria-hidden
                                                        />
                                                    ) : (
                                                        <ChevronRight
                                                            className="size-2.5 shrink-0"
                                                            aria-hidden
                                                        />
                                                    )}
                                                    {formatTimeMx(entry.at)}
                                                </span>
                                            </button>
                                        </TableCell>
                                        <TableCell
                                            title={entry.operationType}
                                            className={cn(
                                                CELL_CLASS,
                                                'break-words text-[10px] font-semibold leading-tight',
                                            )}
                                        >
                                            <BreakableCode value={entry.operationType} />
                                        </TableCell>
                                        <TableCell
                                            title={statusPresentation.title}
                                            className={CELL_CLASS}
                                        >
                                            <span
                                                className={cn(
                                                    'inline-flex rounded-full border px-1 py-px text-[9.5px] font-bold uppercase tracking-[0.03em]',
                                                    statusPresentation.toneClass,
                                                )}
                                            >
                                                {statusPresentation.label}
                                            </span>
                                        </TableCell>
                                        <TableCell
                                            title={errorTitle(entry)}
                                            className={cn(CELL_CLASS, 'break-words')}
                                        >
                                            {(entry.errorMessage ?? entry.errorCode) ? (
                                                <span className="text-[10px] font-semibold leading-tight text-destructive">
                                                    {entry.errorMessage ? (
                                                        entry.errorMessage
                                                    ) : (
                                                        <BreakableCode
                                                            value={entry.errorCode ?? ''}
                                                        />
                                                    )}
                                                </span>
                                            ) : (
                                                <span className="text-muted-foreground">
                                                    {NO_ERROR_VALUE}
                                                </span>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                    {expanded ? (
                                        <TableRow>
                                            <TableCell colSpan={4} className="p-0">
                                                <div
                                                    id={panelId}
                                                    className="space-y-2 bg-muted/30 px-3 py-2"
                                                >
                                                    <div>
                                                        <div className={PRE_LABEL}>
                                                            Folio de la llamada
                                                        </div>
                                                        <p className="mt-1 break-all font-mono text-[10px]">
                                                            {/* eslint-disable-next-line no-restricted-syntax -- folio de la llamada: no hay nombre que resolver y es lo que se cita en el ticket */}
                                                            {entry.callId}
                                                        </p>
                                                    </div>
                                                    <p className={PRE_NOTE}>{NO_PAYLOAD_NOTE}</p>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ) : null}
                                </Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            )}
        </section>
    );
}
